import { faFileAlt } from "@fortawesome/pro-regular-svg-icons";
import { faCommentSmile, faImages, faLock, faSortDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { parse } from "query-string";
import React from "react";

import AboutMe, { AboutMePlaceholder } from "@/mk2/apps/blogs/components/AboutMe";
import BlogFeedPostsWithPager from "@/mk2/apps/blogs/components/BlogFeedPostsWithPager";
import {
    BlogPostCreateFormFactory,
    BlogPostFormData,
    LinkTabItem,
} from "@/mk2/apps/blogs/components/BlogPostCreateForm";
import KidsStage from "@/mk2/apps/blogs/components/KidsStage";
import MoreAboutMe from "@/mk2/apps/blogs/components/MoreAboutMe";
import ProfileIsBlocked from "@/mk2/apps/blogs/components/ProfileIsBlocked";
import ProfileTabs, { Tab } from "@/mk2/apps/blogs/components/ProfileTabs";
import { PROFILE_FEED_MENU_POST_TYPE_FILTER } from "@/mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.constants";
import { checkIfmoreAboutMeHasContent } from "@/mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.helpers";
import {
    blogsAlbumCreateUrl,
    blogsArticleCreateUrl,
    blogsArticleDraftsUrl,
    blogsPostUrl,
    blogsProfileUrl,
} from "@/mk2/apps/blogs/urls";
import { ScrollToPosition } from "@/mk2/apps/groups/containers/GroupSubscribedFeed/GroupSubscribedFeedPage";
import { homepageUrl } from "@/mk2/apps/home/urls";
import AnnouncementBox, { BoxType } from "@/mk2/components/AnnouncementBox";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Failure } from "@/mk2/components/errors/Failure";
import { MenuItem } from "@/mk2/components/menu/MenuItem";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { Menu } from "@/mk2/containers/Menu/Menu";
import { url } from "@/mk2/helpers/urls";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import { BlogPostType } from "@/mk2/schemas";
import { redirectInPWA } from "@/mk2/services/browserHistory";
import {
    BACK_TO_HOMEPAGE,
    BLOGS_FEED_ALL,
    BLOGS_FEED_FILTER_POST_TYPE,
    CREATE_ALBUM,
    DRAFTS,
    HOMEPAGE_POST_TYPE_SHORT_MESSAGE,
    PB_MENU_ALBUMS,
    PB_MENU_POSTS,
    PB_MENU_SHORT_MESSAGES,
    PROFILE__POSTS_FOR_REGISTRED_USERS_ARE_NOT_DISPLAYING_TO_YOU,
    THIS_PHOTOBLOG_IS_STILL_EMPTY,
    WRITE_ARTICLE,
} from "@/mk/autogenerated/translation";
import { isAuthenticated, isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";

import styles from "./ProfileDetail.mscss";
import { Props } from "./ProfileDetailPage";

export const BLOG_POST_CREATE_FORM_NAME = "BLOG_POST_CREATE_FORM";

const fasLock: FontAwesomeIconProps = { icon: faLock };

interface State {
    query: string;
}

class ProfileDetail extends React.Component<Props, State> {
    private textArea: HTMLTextAreaElement;
    private BlogPostCreateForm = BlogPostCreateFormFactory(BLOG_POST_CREATE_FORM_NAME);

    constructor(props: Props) {
        super(props);

        this.state = {
            query: props.query,
        };
    }

    public componentDidMount() {
        const {
            creatingPostABState,
            detailLoadingState,
            feedLoadingState,
            history,
            match: {
                params: { username },
            },
            onScrollTo,
            redirectToPost,
            requestUser,
            profileDetailData,
        } = this.props;

        if (redirectToPost) {
            const gotoUrl = url(blogsPostUrl, { username, postId: redirectToPost });
            redirectInPWA(history, gotoUrl, 301);
            return;
        }

        if (feedLoadingState === LoadingState.SUCCESS_ON_SERVER) {
            const qs = parse(this.props.location.search);
            if (qs.scrollto) {
                onScrollTo(qs.scrollto as ScrollToPosition);
            }
        }

        if (!isSuccess(creatingPostABState.loadingState)) {
            this.loadCreatingPostVariant();
        }

        // reload if data is loaded but partially and user (just) authenticated
        if (
            isAuthenticated(requestUser) &&
            profileDetailData.userRegisteredOn == null &&
            isSuccess(detailLoadingState)
        ) {
            this.loadDetail();
            this.loadFeed();
            this.loadCreatingPostVariant();
        }
    }

    public componentDidUpdate(prevProps: Props) {
        const p = this.props;

        const {
            history,
            match: {
                params: { username },
            },
            redirectToPost,
        } = p;

        if (p.location.search !== prevProps.location.search && p.redirectToPost) {
            const gotoUrl = url(blogsPostUrl, { username, postId: redirectToPost });
            redirectInPWA(history, gotoUrl, 301);
            return;
        }

        if (
            // zmenil sa feed (url) alebo page z feedu
            (p.location.pathname !== prevProps.location.pathname ||
                p.pageRef !== prevProps.pageRef ||
                p.location.search !== prevProps.location.search) &&
            p.loadedAt < Date.now() - 30 * 1000 &&
            isSuccess(p.feedLoadingState)
        ) {
            this.loadFeed();
        }

        if (
            // zmenil sa feed (url) alebo page z feedu
            (p.location.pathname !== prevProps.location.pathname ||
                p.pageRef !== prevProps.pageRef) &&
            // nevraciame sa cez 'Back'
            p.history.action !== "POP" &&
            // a nacitali sme z cache (LoadingSwitch nespustil blogsFeedFetchTrigger)
            isSuccess(p.feedLoadingState)
        ) {
            // defaultne sa scrollne na konci fetchGroupSubscribedFeed sagy, ale ta sa nespusti
            // tak vykonaj scroll side-effect
            const qs = parse(p.location.search);
            if (qs.scrollto) {
                p.onScrollTo(qs.scrollto as ScrollToPosition);
            }
        }
    }

    public render() {
        const {
            anyPageLoaded,
            detailLoadingState,
            feedLoadingState,
            isMobile,
            match: {
                params: { username },
            },
            profileDetailData,
            redirectToPost,
            requestUserPermissions,
            showMoreAboutMe,
        } = this.props;

        if (redirectToPost) {
            return null;
        }

        const isAdmin =
            requestUserPermissions &&
            requestUserPermissions.indexOf("photoblog.can_manage_blogs") >= 0;

        if (profileDetailData.user?.isBlocked) {
            return (
                <ProfileIsBlocked
                    username={username}
                    isAdmin={isAdmin}
                    isBlockedSelf={profileDetailData.user.isBlockedSelf}
                    isLegacyBlocked={profileDetailData.isLegacyBlocked}
                    isLiked={profileDetailData.userIsLiked}
                    onUnlike={this.onUnlike}
                />
            );
        }

        return isMobile && showMoreAboutMe ? (
            <div className={styles.ProfileDetail}>
                <LoadingSwitch
                    loadingState={detailLoadingState}
                    onLoad={this.loadDetail}
                    onRenderSuccess={this.renderLoadSuccessMore}
                    onRenderFailure={this.renderFailureDetail}
                    hasData={profileDetailData.user && anyPageLoaded}
                />
            </div>
        ) : (
            <div className={styles.ProfileDetail}>
                {isMobile && (
                    <LoadingSwitch
                        loadingState={detailLoadingState}
                        onLoad={this.loadDetail}
                        onRenderInit={this.renderInitDetail}
                        onRenderSuccess={this.renderLoadSuccessDetail}
                        onRenderFailure={this.renderFailureDetail}
                        hasData={!!profileDetailData.user}
                    />
                )}
                <LoadingSwitch
                    loadingState={feedLoadingState}
                    onLoad={this.loadFeed}
                    onRenderInit={this.renderLoadSuccessFeed}
                    onRenderSuccess={this.renderLoadSuccessFeed}
                    onRenderFailure={this.renderFailureFeed}
                    hasData={anyPageLoaded}
                />
            </div>
        );
    }

    private loadDetail = () => {
        const {
            match: {
                params: { username },
            },
            onLoadProfileDetail,
        } = this.props;
        onLoadProfileDetail(username);
    };

    private loadFeed = () => {
        const {
            location,
            match: {
                params: { username },
            },
            onLoadProfileFeed,
            pageRef,
            posts,
            postType,
            query,
        } = this.props;
        const numPostsBeforeLoad = posts.length;
        onLoadProfileFeed(username, pageRef, postType, query, location, numPostsBeforeLoad);
    };

    private loadCreatingPostVariant = () => {
        const { creatingPostABState, onLoadCreatingPostVariant } = this.props;

        if (creatingPostABState && !isSuccess(creatingPostABState.loadingState)) {
            onLoadCreatingPostVariant();
        }
    };

    private renderFailureDetail = () => {
        const { detailErrorMessage } = this.props;

        return (
            <Failure
                errorMessage={detailErrorMessage}
                onRetry={this.loadDetail}
                backButtonUrl={homepageUrl}
                backButtonLabel={BACK_TO_HOMEPAGE}
            />
        );
    };

    private renderFailureFeed = () => {
        const { feedErrorMessage } = this.props;

        return (
            <Failure
                errorMessage={feedErrorMessage}
                onRetry={this.loadFeed}
                backButtonUrl={homepageUrl}
                backButtonLabel={BACK_TO_HOMEPAGE}
            />
        );
    };

    private renderInitDetail = () => {
        const {
            isMobile,
            match: {
                params: { username },
            },
        } = this.props;

        return <AboutMePlaceholder isMobile={isMobile} username={username} />;
    };

    private renderLoadSuccessDetail = () => {
        const {
            isMobile,
            location,
            onDeleteAvatarByAdmin,
            onSetLikeUser,
            profileDetailData,
            requestUser,
            requestUserPermissions,
            match: {
                params: { username },
            },
        } = this.props;

        const moreAboutMeHasContent = checkIfmoreAboutMeHasContent(
            isMobile,
            profileDetailData,
            requestUser,
            requestUserPermissions,
        );

        return (
            <React.Fragment>
                <AboutMe
                    isMobile={isMobile}
                    location={location}
                    moreAboutMeHasContent={moreAboutMeHasContent}
                    onDeleteAvatarByAdmin={onDeleteAvatarByAdmin}
                    onSetLikeUser={onSetLikeUser}
                    profileDetailData={profileDetailData}
                    requestUser={requestUser}
                    requestUserPermissions={requestUserPermissions}
                />
                <ProfileTabs
                    username={username}
                    active={Tab.POSTS}
                    postsCount={
                        profileDetailData.userStats.blogsAlbumsCount +
                        profileDetailData.userStats.blogsArticlesCount +
                        profileDetailData.userStats.blogsShortMessagesCount
                    }
                    productsCount={
                        // TODO: Rewrite to be more generic when we start to display more products
                        profileDetailData.userStrollers.length
                    }
                />
            </React.Fragment>
        );
    };

    private renderLoadSuccessFeed = () => {
        const {
            commentsCounts,
            complaintsCounts,
            complaintsOpened,
            creatingPostABState,
            feedErrorMessage,
            feedLoadingState,
            initialValues,
            isIOS,
            isMobile,
            location,
            match,
            match: {
                params: { username },
            },
            numberOfPages,
            onDelete,
            onIgnoreUser,
            onPostToggleMenuMore,
            onPostToggleVisibilityMenuMore,
            onPostTypeFilterMenuMore,
            onReport,
            onSetDisplayingAtFeed,
            onSetVisibility,
            pageRef,
            posts,
            postType,
            profileDetailData,
            requestUser,
            requestUserPermissions,
            userAgent,
        } = this.props;

        const isOwner =
            profileDetailData.user && isAuthenticatedAs(requestUser, profileDetailData.user);

        const oneOrMoreKidsStages = [];
        const chunk = 3;
        for (let i = 0; i < profileDetailData.kids.length; i += chunk) {
            const temparray = profileDetailData.kids.slice(i, i + chunk);
            oneOrMoreKidsStages.push(
                <KidsStage key={`kids-stage-${i}`} kids={temparray} isMobile={isMobile} />,
            );
        }

        const postTypeText =
            {
                [BlogPostType.ALBUM]: PB_MENU_ALBUMS,
                [BlogPostType.ARTICLE]: PB_MENU_POSTS,
                [BlogPostType.AUTOMATIC_MESSAGE]: "automatic_message",
                [BlogPostType.SHARE]: "share",
                [BlogPostType.SHORT_MESSAGE]: PB_MENU_SHORT_MESSAGES,
            }[postType] || BLOGS_FEED_ALL;

        const showFilter =
            Math.min(profileDetailData.userStats.blogsShortMessagesCount, 1) +
                Math.min(profileDetailData.userStats.blogsAlbumsCount, 1) +
                Math.min(profileDetailData.userStats.blogsArticlesCount, 1) >
            1;

        const BlogPostCreateForm = this.BlogPostCreateForm;
        const hideBlogPostCreateForm =
            !isOwner || (creatingPostABState.variant !== "direct" && (isMobile || isIOS));

        const currentUrl = `${location.pathname}${location.search}`;

        const showLinkTabs = !isMobile && !isIOS;
        const linkTabs: LinkTabItem[] | undefined = showLinkTabs
            ? [
                  {
                      icon: faCommentSmile,
                      label: HOMEPAGE_POST_TYPE_SHORT_MESSAGE,
                  },
                  {
                      icon: faImages,
                      label: CREATE_ALBUM,
                      link: url(blogsAlbumCreateUrl, { username }),
                  },
                  {
                      icon: faFileAlt,
                      label: profileDetailData.userStats.blogsArticleDraftsCount
                          ? DRAFTS
                          : WRITE_ARTICLE,
                      link: profileDetailData.userStats.blogsArticleDraftsCount
                          ? url(blogsArticleDraftsUrl, {
                                username,
                                advancedArticleTypeSlug: "article",
                            })
                          : url(blogsArticleCreateUrl, { username }),
                  },
              ]
            : undefined;

        return (
            <React.Fragment>
                {profileDetailData.kids.length > 0 && <>{oneOrMoreKidsStages}</>}
                {!hideBlogPostCreateForm && (
                    <BlogPostCreateForm
                        initialValues={initialValues}
                        isInGroups={false}
                        isIOS={isIOS}
                        isMobile={isMobile}
                        linkTabs={linkTabs}
                        onSave={this.handleOnSavePost}
                        requestUser={requestUser}
                        requestUserPermissions={requestUserPermissions}
                        textareaRef={this.handleTextareaRef}
                    />
                )}
                <div id="scrolltoFirstHere" />{" "}
                {/* musi byt vzdy vyrenderovane v DOMe, pouziva sa zo sagy */}
                {profileDetailData.displayVisibilityAlert ? (
                    <AnnouncementBox
                        message={PROFILE__POSTS_FOR_REGISTRED_USERS_ARE_NOT_DISPLAYING_TO_YOU}
                        hasDetailMessage={false}
                        icon={fasLock}
                        type={BoxType.NoAuthNoContent}
                        currentUrl={currentUrl}
                        marginLeft={isMobile ? 5 : undefined}
                        marginRight={isMobile ? 5 : undefined}
                    />
                ) : (
                    posts.length === 0 &&
                    isSuccess(feedLoadingState) && (
                        <AnnouncementBox
                            message={THIS_PHOTOBLOG_IS_STILL_EMPTY}
                            hasDetailMessage={false}
                            type={BoxType.White}
                            marginLeft={isMobile ? 5 : undefined}
                            marginRight={isMobile ? 5 : undefined}
                        />
                    )
                )}
                {showFilter && profileDetailData.user && (
                    <div className={styles.ProfileDetail__postTypeFilter}>
                        <Menu
                            menuId={PROFILE_FEED_MENU_POST_TYPE_FILTER}
                            trigger={
                                <Btn
                                    onClick={onPostTypeFilterMenuMore}
                                    type={BtnType.GreyText}
                                    label={
                                        <div
                                            className={
                                                styles.ProfileDetail__postTypeFilter__trigger
                                            }
                                        >
                                            <span
                                                className={
                                                    styles.ProfileDetail__postTypeFilter__label
                                                }
                                            >
                                                {BLOGS_FEED_FILTER_POST_TYPE}:
                                            </span>
                                            &nbsp;
                                            {postTypeText}
                                            &nbsp;
                                            <FontAwesomeIcon
                                                icon={faSortDown}
                                                className={
                                                    styles.ProfileDetail__postTypeFilter__icon
                                                }
                                            />
                                        </div>
                                    }
                                />
                            }
                            triggerShadow={false}
                        >
                            <MenuItem
                                label={BLOGS_FEED_ALL}
                                link={url(blogsProfileUrl, { username })}
                            />
                            {profileDetailData.userStats.blogsShortMessagesCount > 0 && (
                                <MenuItem
                                    label={`${PB_MENU_SHORT_MESSAGES} (${profileDetailData.userStats.blogsShortMessagesCount})`}
                                    link={url(
                                        blogsProfileUrl,
                                        { username },
                                        { post_type: BlogPostType.SHORT_MESSAGE },
                                    )}
                                />
                            )}
                            {profileDetailData.userStats.blogsAlbumsCount > 0 && (
                                <MenuItem
                                    label={`${PB_MENU_ALBUMS} (${profileDetailData.userStats.blogsAlbumsCount})`}
                                    link={url(
                                        blogsProfileUrl,
                                        { username },
                                        { post_type: BlogPostType.ALBUM },
                                    )}
                                />
                            )}
                            {profileDetailData.userStats.blogsArticlesCount > 0 && (
                                <MenuItem
                                    label={`${PB_MENU_POSTS} (${profileDetailData.userStats.blogsArticlesCount})`}
                                    link={url(
                                        blogsProfileUrl,
                                        { username },
                                        { post_type: BlogPostType.ARTICLE },
                                    )}
                                />
                            )}
                        </Menu>
                    </div>
                )}
                <BlogFeedPostsWithPager
                    category={null}
                    commentsCounts={commentsCounts}
                    complaintsCounts={complaintsCounts}
                    complaintsOpened={complaintsOpened}
                    disablePostAuthorBadges
                    errorMessage={feedErrorMessage}
                    hashtags={null}
                    history={this.props.history}
                    isMobile={isMobile}
                    loadingState={feedLoadingState}
                    location={location}
                    match={match}
                    numberOfPages={numberOfPages}
                    onDelete={onDelete}
                    onIgnoreUser={onIgnoreUser}
                    onPostToggleMenuMore={onPostToggleMenuMore}
                    onPostToggleVisibilityMenuMore={onPostToggleVisibilityMenuMore}
                    onReport={onReport}
                    onSetDisplayingAtFeed={onSetDisplayingAtFeed}
                    onSetVisibility={onSetVisibility}
                    pageRef={pageRef}
                    permissions={requestUserPermissions}
                    posts={posts}
                    requestUser={requestUser}
                    showHashtagsBox={false}
                    showStrips
                    userAgent={userAgent}
                />
                <div id="scrolltoLastHere" />{" "}
                {/* musi byt vzdy vyrenderovane v DOMe, pouziva sa zo sagy */}
            </React.Fragment>
        );
    };

    private renderLoadSuccessMore = () => {
        const {
            isMobile,
            location,
            onExpandAvatarGrid,
            onExpandGroupList,
            profileDetailData,
            requestUser,
            requestUserPermissions,
        } = this.props;

        return (
            <div className={styles["ProfileDetail--moreAboutMe"]}>
                {isMobile && isAuthenticated(requestUser) && (
                    <MoreAboutMe
                        isMobile={isMobile}
                        location={location}
                        onExpandAvatarGrid={onExpandAvatarGrid}
                        onExpandGroupList={onExpandGroupList}
                        profileDetailData={profileDetailData}
                        requestUser={requestUser}
                        requestUserPermissions={requestUserPermissions}
                    />
                )}
            </div>
        );
    };

    private onUnlike = () => {
        const { profileDetailData, onSetLikeUser } = this.props;
        if (!!profileDetailData && profileDetailData.user) {
            onSetLikeUser(profileDetailData.user.username, false);
        }
    };

    private handleTextareaRef = (ref: HTMLTextAreaElement) => {
        this.textArea = ref;
    };

    private handleOnSavePost = (values: BlogPostFormData) => {
        const {
            match: {
                params: { username },
            },
            onAddPost,
        } = this.props;
        onAddPost(username, values);
    };
}

export default ProfileDetail;
